import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Tr } from '@utils';
import { Organization } from '@services';
import { Grid, Typography } from '@mui/material';
import { EntityList, EntityListItem, EntityListItemCell, IEntityListProps } from '@components/common';
import { useLoggedinUser } from '@hooks/useLoggedingUser';

type EntityType = Organization;

export interface IOrganizationListProps extends IEntityListProps<EntityType> {
	paddingX?: number | string;
	entities: EntityType[];
	selected: EntityType[];
	onSelectChange?: (selected: EntityType[]) => void;
	onView?: (entity: EntityType) => void;
	onEdit?: (entity: EntityType) => void;
	onDelete?: (entity: EntityType) => void;
	onAssignRole?: (entity: EntityType) => void;
	onUploadVehicleList?: (entity: EntityType) => void;
}

export const OrganizationList: FC<IOrganizationListProps> = ({
	entities,
	selected,
	onSelectChange,
	onView,
	onEdit,
	onDelete,
	onAssignRole,
	onUploadVehicleList,
	paddingX,
	...props
}) => {
	const { t } = useTranslation('portal');
	const user = useLoggedinUser();
	const isAdmin = user?.isAdmin() || false;
	const handleSelectChange = (status: boolean, entity: EntityType) => {
		const newSelections = selected.filter((s) => s.id !== entity.id);
		status && newSelections.push(entity);
		onSelectChange?.(newSelections);
	};

	return (
		<EntityList
			entities={entities}
			selected={selected}
			onSelectChange={onSelectChange}
			hideSelectAllButton={!isAdmin}
			paddingX={paddingX}
			{...props}
		>
			{entities.map((entity, i) => (
				<EntityListItem
					key={`entity-${entity.id}-${i}`}
					sx={{ paddingRight: paddingX }}
					selected={!!selected.find((s) => s.id === entity.id)}
					actions={[
						{ text: <Tr.Portal path="view" />, onClick: () => onView?.(entity) },
						{ text: <Tr.Portal path="edit" />, onClick: () => onEdit?.(entity), hidden: !isAdmin },
						{ text: <Tr.Portal path="delete" />, onClick: () => onDelete?.(entity), hidden: !isAdmin },
						{ text: <Tr.Portal path="organizations.assign-role" />, onClick: () => onAssignRole?.(entity), hidden: !isAdmin },
						{
							text: <Tr.Portal path="organizations.upload-vehicle-list" />,
							onClick: () => onUploadVehicleList?.(entity),
							hidden: !isAdmin
						}
					]}
					notSelectable={!isAdmin}
					onSelectChange={(status) => handleSelectChange(status, entity)}
				>
					<Grid container>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="organizations.organization" />}>
								<Typography>{entity.name}</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="organizations.product-types" />}>
								<Typography>
									{[
										entity.isVehicleSeller ? t('organizations.product-vehicle') : '',
										entity.isInfraSeller ? t('organizations.product-infrastructure') : ''
									]
										.filter(Boolean)
										.join(', ')}
								</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="organizations.business-type" />}>
								<Typography>
									<Tr.Portal path={entity.isB2CSeller ? 'organizations.business-b2c' : 'organizations.business-b2b'} />
								</Typography>
							</EntityListItemCell>
						</Grid>
						<Grid item xs={3}>
							<EntityListItemCell title={<Tr.Portal path="organizations.vat-code" />}>
								<Typography>{entity.vat}</Typography>
							</EntityListItemCell>
						</Grid>
					</Grid>
				</EntityListItem>
			))}
		</EntityList>
	);
};
